'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { Shield, Lock, ArrowRight, User, Loader2 } from 'lucide-react';
import { useCorporate } from '@/context/CorporateContext';
import { supabase } from '@/lib/supabaseClient';
import { useWeb3 } from '@/context/Web3Context';
import { LiquidAmbientBackground } from '@/components/ui/LiquidGlass';

export default function LoginPage() {
    const { config: corporateConfig } = useCorporate();
    const router = useRouter();
    const web3Context = useWeb3();
    const web3login = web3Context?.login;

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const authProvider = corporateConfig?.providers?.auth || 'SUPABASE_AUTH';

    const handleSupabaseLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            // 1. Autenticación en la capa global de Supabase
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            toast.success("Autenticación exitosa. Verificando perfil corporativo...", { id: 'login-toast' });

            const targetCorporateId = (corporateConfig?.id && corporateConfig.id !== 'master-root')
                ? corporateConfig.id
                : (process.env.NEXT_PUBLIC_MASTER_CORPORATE_ID || '');

            const jitResponse = await fetch('/api/auth/callback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-corporate-id': targetCorporateId
                },
                body: JSON.stringify({
                    userId: data.user.id,
                    email: data.user.email,
                    corporateId: targetCorporateId,
                    accessToken: data.session?.access_token,
                    platform_context: 'WEB_B2B' // Contexto para asegurar el namespace BUSINESS en el Ledger
                })
            });

            const jitData = await jitResponse.json();

            if (!jitResponse.ok) {
                throw new Error(jitData.error || 'Error en el Onboarding Corporativo');
            }

            // 3. Redirigir al usuario según su rol y contexto resuelto en base de datos
            window.dispatchEvent(new Event('auth_changed'));

            if (jitData.redirect) {
                router.push(jitData.redirect);
            } else {
                router.push('/hub');
            }

        } catch (error: any) {
            console.error("Login Error:", error);
            toast.error(`Error: ${error.message}`, { id: 'login-toast', duration: 7000 });
        } finally {
            setIsLoading(false);
        }
    };


    const handleWeb3AuthLogin = async () => {
        setIsLoading(true);
        toast.loading("Inicializando SDK de Web3Auth...", { id: 'web3auth-toast' });

        try {
            if (web3login) {
                const status = await web3login();
                if (status === 'success') {
                    toast.success("Autenticación Web3 completada.", { id: 'web3auth-toast' });
                    router.push('/wallet');
                } else if (status === 'onboarding') {
                    toast.success("Redirigiendo al Onboarding...", { id: 'web3auth-toast' });
                    // Routing is handled natively by the context hook but we assure UX feedback
                } else {
                    toast.dismiss('web3auth-toast');
                }
            } else {
                toast.error("Web3Auth Context Error", { id: 'web3auth-toast' });
            }
        } catch (err: any) {
            toast.error("Error al iniciar Web3Auth: " + err.message, { id: 'web3auth-toast' });
        } finally {
            setIsLoading(false);
        }
    };

    if (!corporateConfig) {
        return (
            <div className="min-h-screen bg-background flex flex-col justify-center items-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col justify-center items-center py-12 px-6 lg:px-8 absolute inset-0 z-50 font-sans overflow-hidden">
            <LiquidAmbientBackground intensity="vibrant" />

            <div className="sm:mx-auto sm:w-full sm:max-w-md text-center relative z-10 animate-in fade-in slide-in-from-top-4 duration-700">
                <div className="flex justify-center mb-8">
                    <div className="size-20 liquid-glass-tint rounded-3xl shadow-2xl flex items-center justify-center p-4 transition-colors"
                        style={{ background: 'linear-gradient(to bottom right, var(--tenant-primary), var(--tenant-secondary))' }}>
                        <Shield className="text-white size-10" />
                    </div>
                </div>
                <h2 className="text-4xl font-black text-foreground tracking-tighter uppercase whitespace-nowrap">
                    {corporateConfig?.name || 'Portal'} Vault
                </h2>
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-1 rounded-full liquid-glass">
                    <Shield className="size-3 text-slate-300" />
                    <span className="text-[10px] text-slate-300 font-black uppercase tracking-[0.3em]">Acceso Encriptado</span>
                </div>
            </div>

            <div className="mt-12 sm:mx-auto sm:w-full sm:max-w-md relative z-10 animate-in fade-in slide-in-from-bottom-6 duration-1000">
                <div className="liquid-glass-elevated p-10 rounded-[3rem] relative overflow-hidden group">

                    {authProvider === 'SUPABASE_AUTH' ? (
                        <form className="space-y-8" onSubmit={handleSupabaseLogin}>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-black text-slate-300 uppercase tracking-widest ml-1">
                                    <User className="size-3" />
                                    Correo Institucional
                                </label>
                                <div className="relative group/input">
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="liquid-glass-input block w-full px-6 py-4 rounded-2xl text-foreground placeholder-slate-400 focus:outline-none transition-all text-sm font-semibold tracking-tight"
                                        placeholder="usuario@dominio.com"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-[10px] font-black text-slate-300 uppercase tracking-widest ml-1">
                                    <Lock className="size-3" />
                                    Contraseña
                                </label>
                                <div className="relative group/input">
                                    <input
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="liquid-glass-input block w-full px-6 py-4 rounded-2xl text-foreground placeholder-slate-400 focus:outline-none transition-all text-sm font-bold tracking-[0.2em]"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    style={{ backgroundColor: 'var(--tenant-primary)' }}
                                    className={`liquid-glass-button w-full relative overflow-hidden flex justify-center items-center py-5 px-6 rounded-2xl transition-all ${isLoading ? 'opacity-70' : 'hover:opacity-90 active:scale-[0.98]'}`}
                                >
                                    <span className="flex items-center gap-3 text-xs font-black text-white uppercase tracking-[0.2em] relative z-10">
                                        {isLoading ? <Loader2 className="size-4 animate-spin" /> : 'Ingresar al Ecosistema'}
                                        {!isLoading && <ArrowRight className="size-4" />}
                                    </span>
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-8 flex flex-col items-center justify-center py-4">
                            <div className="text-center">
                                <h3 className="text-lg font-black text-foreground mb-2">Conexión Descentralizada</h3>
                                <p className="text-xs text-slate-400 font-medium">Este portal utiliza tecnología Web3Auth para acceso sin contraseñas.</p>
                            </div>

                            <button
                                type="button"
                                onClick={handleWeb3AuthLogin}
                                disabled={isLoading}
                                className={`liquid-glass-button w-full relative overflow-hidden flex justify-center items-center py-5 px-6 rounded-2xl transition-all ${isLoading ? 'opacity-70' : 'hover:bg-white/10 active:scale-[0.98]'}`}
                            >
                                <span className="flex items-center gap-3 text-xs font-black text-foreground uppercase tracking-[0.2em]">
                                    {isLoading ? <Loader2 className="size-4 animate-spin text-[var(--tenant-primary)]" /> : 'Continuar con Web3Auth'}
                                </span>
                            </button>
                        </div>
                    )}
                </div>

                <p className="mt-8 text-center text-[10px] text-slate-400 font-black uppercase tracking-[0.3em] opacity-60 hover:opacity-100 transition-opacity cursor-default">
                    WaaS Network © 2026 • Tenant Independiente
                </p>
            </div>
        </div>
    );
}
